// ExchangeModal — the Deposit / Buy / Convert dialog, on Soft.
// Opens on a given tab (header Deposit button / Wallet popover), closes on
// Close, Escape or a backdrop click, locks body scroll while open, resets to
// the requested tab.
//
// THE FORMAT is the operator's reference (2026-10-13: Axiom's Exchange,
// Trojan's Deposit — "do it as the others do"); the style is ours:
// - the head: the dialog's name and Close; under it the three tabs across the
//   width, each with its mark;
// - Deposit: the real QR at the left (depositQrModules.ts), at its right the
//   label and the address as plain wrapped text of normal size, its two ends
//   stressed, whose wallet it is under it; the network in a line; one
//   full-width "Copy address". While the wallet list has not answered it says
//   so — not "create a wallet";
// - no line of description under the tabs (round 6, fourth pass: "what for a
//   heap of text"): a tab's name says what it does, and the one fact that is
//   needed — the network — stands once, at the deposit address;
// - Buy and Convert: two LARGE field cards (a label at the top left, the
//   figure as the field itself, the token capsule at the right), the flip
//   between, one full-width action;
// - the token picker is a capsule in the card and a Soft menu: mark + ticker
//   + full name + check on the chosen one;
// - the action is stateful: "Enter amount" (disabled) until the number is real.
//
// One arrangement per tab: the operator's pick (2026-10-18: Soft for all
// four money dialogs; the review alternatives are gone).
//
// MONEY PATH. Deposit reads the address, what Copy writes and what the QR
// says from ONE hook (useDeposit.ts). Buy and Convert have no handler and
// make no request — as before.
//
// Honest data: Deposit uses the authenticated account's active primary wallet.
// GRAM(=TON)/USD rate is REAL (useGramUsdRate, ADR-026); USDT/USD pegged 1:1.
// Until the rate loads, estimates are "—".

import {
  ArrowDownIcon,
  ArrowsDownUpIcon,
  ArrowsLeftRightIcon,
  CaretDownIcon,
  CheckIcon,
  CopyIcon,
  CreditCardIcon,
  XIcon,
} from '@phosphor-icons/react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { formatTon } from '../../hooks/useLiveWalletBalance'
import { usePopoverDismiss } from '../../hooks/usePopoverDismiss'
import soft from '../../styles/soft.module.css'
import { figs } from '../ui/fig'
import { GramMark } from '../ui/icons/GramMark'
import { UsdtMark } from '../ui/icons/UsdtMark'
import {
  ACCEPTS_NOTE,
  AddressLoading,
  AddressText,
  CopiedStatus,
  DepositWallet,
  NETWORK_NOTE,
  QrPlate,
} from './depositParts'
import styles from './exchange.module.css'
import { EuFlag, TonLogo, UsFlag } from './icons'
import { type ExchangeTab, ModeMark, TABS } from './modes'
import { useDeposit } from './useDeposit'

export type { ExchangeTab } from './modes'

/** The balances the header already holds for the selected wallets — Buy and
 *  Convert make no request of their own (Deposit's wallet menu reads each
 *  wallet's own GRAM for its row: depositParts). `gramNano`: nanotons, null
 *  until one is read. `usdt`: the USDT holding as the header formats it, ''
 *  until one is read. */
export interface ExchangeBalances {
  gramNano: string | null
  usdt: string
}

interface Props {
  open: boolean
  tab: ExchangeTab
  gramUsdRate: number | null
  /** drawn at a card's top right; nothing is drawn for a balance not yet read */
  balances?: ExchangeBalances
  onClose: () => void
}

// ---- tokens: mark + full name (picker rows read like beui wallet rows).
// Currencies are GRAM and USDT only (user: no TON as a currency — TON is
// the network; its logo lives on the QR badge). Buy pays with fiat — the
// pay side is a picker too (user: «выбор на что покупать», chevron как у
// GRAM); EUR has no rate feed yet → its estimates stay honest «—». ----
type Token = 'GRAM' | 'USDT' | 'USD' | 'EUR'
const CRYPTO: Token[] = ['GRAM', 'USDT']
const FIAT: Token[] = ['USD', 'EUR']
const TOKEN_NAME: Record<Token, string> = {
  GRAM: 'Gram',
  USDT: 'Tether USD',
  USD: 'US Dollar',
  EUR: 'Euro',
}
const FIAT_SYMBOL: Record<string, string> = { USD: '$', EUR: '€' }

function TokenIcon({ token, size = 20 }: { token: Token; size?: number }) {
  if (token === 'GRAM') return <GramMark size={size} />
  if (token === 'USDT') return <UsdtMark size={size} />
  if (token === 'EUR') return <EuFlag size={size} />
  return <UsFlag size={size} />
}

// GRAM trades at the live rate; USDT=USD peg 1:1 → USD per one unit.
// EUR: no EUR/USD feed yet — null keeps estimates at an honest «—».
const usdPerUnit = (token: Token, rate: number | null): number | null => {
  if (token === 'USDT' || token === 'USD') return 1
  if (token === 'EUR') return null
  return rate // GRAM
}

const fmt = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 })
const fmtRate = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 4 })
// what was typed, as the action repeats it: grouped, with every digit it has
const fmtTyped = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 9 })
const sanitize = (s: string) => {
  const c = s.replace(/[^\d.]/g, '')
  const dot = c.indexOf('.')
  const out = dot === -1 ? c : `${c.slice(0, dot)}.${c.slice(dot + 1).replace(/\./g, '')}`
  return out.slice(0, 15)
}
const toNum = (s: string) => {
  const n = Number(s)
  return Number.isFinite(n) ? n : 0
}
// a token's balance as text, or null while there is none to show: a balance
// not yet read is not a zero and not a dash, and the dialog knows no fiat one
const balanceOf = (token: Token, balances: ExchangeBalances | undefined): string | null => {
  if (!balances) return null
  if (token === 'GRAM') return balances.gramNano !== null ? formatTon(balances.gramNano) : null
  if (token === 'USDT') return balances.usdt !== '' ? balances.usdt : null
  return null
}
// drawing only: a long figure is set in a smaller size so that it is whole
const sizeOf = (text: string) => (text.length > 16 ? 's' : text.length > 11 ? 'm' : undefined)

export function ExchangeModal({ open, tab: initialTab, gramUsdRate, balances, onClose }: Props) {
  const ref = useRef<HTMLDialogElement>(null)
  const downOnBackdrop = useRef(false)
  const [tab, setTab] = useState<ExchangeTab>(initialTab)
  const rate = gramUsdRate

  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (open) {
      setTab(initialTab)
      if (!d.open) d.showModal()
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = prev
      }
    }
    if (d.open) d.close()
  }, [open, initialTab])

  const toBuy = () => setTab('buy')

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop pointer-dismiss; keyboard close is native Escape
    <dialog
      ref={ref}
      className={styles.dialog}
      aria-label="Exchange"
      onClose={onClose}
      onMouseDown={(e) => {
        downOnBackdrop.current = e.target === ref.current
      }}
      onClick={(e) => {
        if (e.target === ref.current && downOnBackdrop.current) onClose()
      }}
    >
      {open && (
        <div className={styles.surface}>
          {/* the head: the dialog's name and Close */}
          <div className={styles.head}>
            <span className={styles.headMark} aria-hidden="true">
              <ArrowsLeftRightIcon size={18} weight="bold" aria-hidden="true" />
            </span>
            <h2 className={styles.title}>Exchange</h2>
            <button
              type="button"
              className={`${soft.btn} ${soft.round} ${soft.small} ${styles.close}`}
              onClick={onClose}
              aria-label="Close exchange"
            >
              <XIcon size={16} weight="bold" aria-hidden="true" />
            </button>
          </div>

          {/* the mode switch: the product's own control for such a choice — the
              Soft track and its segments (soft.track / soft.seg), across the
              width as Page settings' categories are, each with its mark */}
          <div className={`${soft.track} ${styles.modes}`} role="tablist">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                className={`${soft.seg} ${styles.mode}`}
                aria-selected={t.key === tab}
                data-on={t.key === tab}
                onClick={() => setTab(t.key)}
              >
                <ModeMark tab={t.key} size={15} />
                {figs(t.label)}
              </button>
            ))}
          </div>

          {/* what gives way on a short window: everything under the tabs.
              key remounts the body per tab → enter animation + clean state */}
          <div className={styles.scroll}>
            <div className={styles.tabPane} key={tab}>
              {tab === 'deposit' && <DepositBody onBuy={toBuy} />}
              {tab === 'buy' && <BuyBody rate={rate} balances={balances} />}
              {tab === 'convert' && <ConvertBody rate={rate} balances={balances} />}
            </div>
          </div>
        </div>
      )}
    </dialog>
  )
}

// ---- token picker: pill trigger + morphing panel (beui wallet-card manner).
// A row says what it is (mark, ticker, name) and, at its right, what the
// wallets hold of it once that is read (round 9, the operator: "the balance
// when I open a currency") — the figure the card's own Balance shows, from
// the same header read. Fiat has none here: nothing is drawn for it. --
function TokenSelect({
  value,
  options,
  onChange,
  ariaLabel,
  drop = 'down',
  balances,
}: {
  value: Token
  options: Token[]
  onChange: (t: Token) => void
  ariaLabel: string
  /** 'up' for pickers near the modal bottom — the surface clips overflow. */
  drop?: 'down' | 'up'
  balances?: ExchangeBalances
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  usePopoverDismiss(wrapRef, open, () => setOpen(false), btnRef)
  // Escape with the menu open is the menu's: the hook above shuts it, and the
  // key's default — the native dialog's own close, which would take what was
  // typed with it — is cancelled. With the menu shut the key is the dialog's.
  // In the capture phase: the hook's own listener shuts the menu, and the
  // render that follows removes this one before a later listener would run.
  useEffect(() => {
    if (!open) return
    const keepDialog = (e: KeyboardEvent) => {
      if (e.key === 'Escape') e.preventDefault()
    }
    document.addEventListener('keydown', keepDialog, true)
    return () => document.removeEventListener('keydown', keepDialog, true)
  }, [open])

  // single option → static pill, nothing to pick
  if (options.length < 2) {
    return (
      <span className={styles.pillStatic}>
        <TokenIcon token={value} size={22} />
        <span className={styles.pillTicker}>{value}</span>
      </span>
    )
  }

  return (
    <div className={styles.pickerWrap} ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        className={`${styles.pill} ${open ? styles.pillOpen : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
      >
        <TokenIcon token={value} size={22} />
        <span className={styles.pillTicker}>{value}</span>
        <CaretDownIcon size={12} weight="bold" className={styles.pillChevron} aria-hidden="true" />
      </button>
      {open && (
        <div
          className={`${soft.menu} ${styles.pickerMenu} ${drop === 'up' ? styles.pickerMenuUp : ''}`}
          role="menu"
        >
          {options.map((t) => {
            const held = balanceOf(t, balances)
            return (
              <button
                key={t}
                type="button"
                role="menuitemradio"
                aria-checked={t === value}
                className={styles.pickerRow}
                onClick={() => {
                  onChange(t)
                  setOpen(false)
                }}
              >
                <TokenIcon token={t} size={24} />
                <span className={styles.pickerNames}>
                  <span className={styles.pickerTicker}>{t}</span>{' '}
                  <span className={styles.pickerName}>{TOKEN_NAME[t]}</span>
                </span>
                {held !== null ? (
                  <span className={styles.pickerHeld} data-part="menu-held">
                    {' '}
                    {held}
                  </span>
                ) : null}
                {/* the check's place is kept on every row, so the figures
                    stand in one column */}
                <span className={styles.pickerCheckSlot} aria-hidden="true">
                  {t === value && (
                    <CheckIcon
                      size={14}
                      weight="bold"
                      className={styles.pickerCheck}
                      aria-hidden="true"
                    />
                  )}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---- Deposit — the authenticated account's active primary wallet ----
// Axiom's arrangement: one card — the QR at the left, the address at its
// right as plain text, the wallet it is of at the column's foot, the network
// as the card's last line; one full-width Copy. Portfolio's Wallets panel
// (round 9) draws the same body `flush` in its own frame, without the way to
// Buy.
export function DepositBody({ onBuy, flush = false }: { onBuy?: () => void; flush?: boolean }) {
  const deposit = useDeposit()
  const { loading, address, groups, qrText, copy, announced } = deposit

  return (
    <div className={`${styles.body} ${styles.bodyDeposit} ${flush ? styles.bodyFlush : ''}`}>
      <div className={styles.depositCard}>
        <div className={styles.depositMain}>
          <QrPlate text={qrText} loading={loading} />
          <div className={styles.addressCol}>
            {/* the coin's mark before its words, in their colour (operator,
                2026-10-18: "the GRAM icon in the same colour") */}
            <span className={`${styles.label} ${styles.addressLabel}`}>
              <GramMark knockout size={12} />
              {figs('GRAM deposit address')}
            </span>
            {loading ? (
              <AddressLoading />
            ) : address ? (
              <AddressText address={address} groups={groups} />
            ) : (
              <span className={styles.addressEmpty}>
                Create a wallet to get your deposit address.
              </span>
            )}
            {/* the column's foot, on the QR's lower edge: whose address it is
                — the wallet's name and the primary's gold dot — and, with more
                than one wallet, the choice of it. No copy mark here: the
                full-width action under the card is the copy. */}
            <DepositWallet deposit={deposit} className={styles.ownerFoot} />
          </div>
        </div>

        {/* the network: the one fact to know before sending, the card's own
            last line (round 9, operator: the line under the card stood on
            another tone) — one tone, the network in its weight */}
        <p className={styles.netNote}>
          <TonLogo size={12} className={styles.netTon} />
          <span>
            <b>{NETWORK_NOTE}</b> · {ACCEPTS_NOTE}
          </span>
        </p>
      </div>

      {/* the one action of this tab, as Buy and Convert have theirs. It
          never changes: once the address is on the clipboard the toast says
          so (ui/copyToast, the product's one copy answer) */}
      <button
        type="button"
        className={`${soft.action} ${styles.cta} ${styles.copy}`}
        disabled={!address}
        aria-label="Copy deposit address"
        onClick={copy}
      >
        <CopyIcon size={17} weight="bold" aria-hidden="true" />
        {figs('Copy address')}
      </button>
      <CopiedStatus text={announced} />

      {/* the other way in, under the action */}
      {onBuy ? (
        <div className={styles.helperRow}>
          <span className={styles.helperText}>No GRAM yet?</span>
          <button type="button" className={styles.helperLink} onClick={onBuy}>
            Buy with card
          </button>
        </div>
      ) : null}
    </div>
  )
}

interface BodyProps {
  rate: number | null
  balances: ExchangeBalances | undefined
}

// a wallet's balance of one token, at a card's top right — or nothing
function Balance({ token, balances }: { token: Token; balances: ExchangeBalances | undefined }) {
  const value = balanceOf(token, balances)
  if (value === null) return null
  return (
    <span className={styles.balance} data-part="balance">
      {figs('Balance ')}
      <b>
        {/* the coin's mark, not its word (operator, 2026-10-18: "it is not
            clear what balance it is"); its word is said, not drawn */}
        <TokenIcon token={token} size={13} />
        {figs(value)}
      </b>
      <span className={styles.srOnly}> {token}</span>
    </span>
  )
}

// ---- the result's card, Buy's and Convert's: the figure the dialog computed
// (a dash while it cannot), its token's capsule, and under the figure the rate
// it was computed at. Drawing only. ----
function ResultCard({
  label,
  value,
  rate,
  token,
  balances,
  children,
}: {
  label: string
  value: number | null
  rate: string
  token: Token
  balances: ExchangeBalances | undefined
  /** the token's capsule */
  children: ReactNode
}) {
  const text = value != null ? fmt(value) : '—'
  return (
    <div className={styles.card} data-part="side">
      <div className={styles.cardHead}>
        <span className={styles.label}>{label}</span>
        <Balance token={token} balances={balances} />
      </div>
      <div className={styles.cardRow}>
        <span className={styles.amountOut} data-zero={value == null} data-size={sizeOf(text)}>
          {text}
        </span>
        {children}
      </div>
      <p className={styles.cardNote} data-part="rate">
        {rate}
      </p>
    </div>
  )
}

// ---- Buy (pay fiat → receive crypto) ----
// on-ramp минимум (манера Axiom «Minimum: 20 USD»); ниже минимума CTA
// disabled и подпись краснеет
const BUY_MIN = 20

function BuyBody({ rate, balances }: BodyProps) {
  const payInputRef = useRef<HTMLInputElement>(null)
  const [usd, setUsd] = useState('')
  const [payTok, setPayTok] = useState<Token>('USD')
  const [receiveTok, setReceiveTok] = useState<Token>('GRAM')
  const n = toNum(usd)
  const per = usdPerUnit(receiveTok, rate)
  const payPer = usdPerUnit(payTok, rate) // USD 1, EUR — honest null (no feed)
  const receive = per != null && per > 0 && payPer != null && n > 0 ? (n * payPer) / per : null
  const sym = FIAT_SYMBOL[payTok] ?? '$'
  const belowMin = n > 0 && n < BUY_MIN

  return (
    <>
      <div className={styles.body}>
        {/* the two sides, each its own card */}
        <div className={styles.sides} data-part="sides">
          {/* a click anywhere on the card puts the caret in its field */}
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: the click only forwards the focus; the keyboard works through the field itself */}
          <div
            className={`${styles.card} ${styles.cardType}`}
            data-part="side"
            onClick={(e) => {
              if ((e.target as HTMLElement).closest('button')) return
              payInputRef.current?.focus()
            }}
          >
            <div className={styles.cardHead}>
              <span className={styles.label}>You pay</span>
            </div>
            <div className={styles.cardRow}>
              <input
                ref={payInputRef}
                className={styles.amount}
                data-size={sizeOf(usd)}
                value={usd}
                placeholder="0"
                onChange={(e) => setUsd(sanitize(e.target.value))}
                inputMode="decimal"
                aria-label={`Amount to pay (${payTok})`}
              />
              <TokenSelect
                value={payTok}
                options={FIAT}
                onChange={setPayTok}
                ariaLabel="Pay currency"
              />
            </div>
            {/* what the field must hold, under its figure */}
            <p className={styles.cardNote}>
              <span className={styles.headData} data-warn={belowMin}>
                Min {sym}
                {BUY_MIN}
              </span>
            </p>
          </div>

          {/* exactly in the middle between the two cards: Buy goes one way, so
              it is a mark on the same seat as Convert's flip, not a button */}
          <div className={styles.between} data-part="between">
            <span className={styles.seat}>
              <span className={styles.betweenBtn} data-static="true">
                <ArrowDownIcon size={18} weight="bold" aria-hidden="true" />
              </span>
            </span>
          </div>

          <ResultCard
            label="You receive"
            value={receive}
            token={receiveTok}
            balances={balances}
            rate={
              per != null && payPer != null
                ? `1 ${receiveTok} ≈ ${sym}${fmtRate(per / payPer)}`
                : `1 ${receiveTok} ≈ ${sym}—`
            }
          >
            <TokenSelect
              value={receiveTok}
              options={CRYPTO}
              onChange={setReceiveTok}
              ariaLabel="Receive token"
              drop="up"
              balances={balances}
            />
          </ResultCard>
        </div>
      </div>
      <div className={styles.footer}>
        <button type="button" className={`${soft.action} ${styles.cta}`} disabled={n < BUY_MIN}>
          {n <= 0 ? (
            'Enter amount'
          ) : belowMin ? (
            `Minimum ${sym}${BUY_MIN}`
          ) : (
            <>
              <CreditCardIcon size={17} weight="bold" aria-hidden="true" />
              {/* what will happen: the estimate the card shows, in its words */}
              {figs(receive != null ? `Buy ${fmt(receive)} ${receiveTok}` : `Buy ${receiveTok}`)}
            </>
          )}
        </button>
      </div>
    </>
  )
}

// ---- Convert (crypto → crypto) ----
function ConvertBody({ rate, balances }: BodyProps) {
  const fromInputRef = useRef<HTMLInputElement>(null)
  const [amount, setAmount] = useState('')
  const [fromTok, setFromTok] = useState<Token>('GRAM')
  const [toTok, setToTok] = useState<Token>('USDT')
  const [flips, setFlips] = useState(0)
  const n = toNum(amount)
  const fromPer = usdPerUnit(fromTok, rate)
  const toPer = usdPerUnit(toTok, rate)
  const usdWorth = fromPer != null && n > 0 ? n * fromPer : null
  const out = usdWorth != null && toPer != null && toPer > 0 ? usdWorth / toPer : null

  const flip = () => {
    setFromTok(toTok)
    setToTok(fromTok)
    setFlips((f) => f + 1)
  }
  // picking the opposite side's token swaps the pair instead of duplicating it
  const pickFrom = (t: Token) => (t === toTok ? flip() : setFromTok(t))
  const pickTo = (t: Token) => (t === fromTok ? flip() : setToTok(t))

  // cross rate: how much of `toTok` one `fromTok` is worth
  const cross = fromPer != null && toPer != null && toPer > 0 ? fromPer / toPer : null

  return (
    <>
      <div className={styles.body}>
        {/* the two sides, each its own card */}
        <div className={styles.sides} data-part="sides">
          {/* клик по ЛЮБОМУ месту карточки фокусирует инпут (фидбек: тыкать
              точно в цифры неудобно) */}
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: клик — только удобный фокус-проброс; клавиатура работает через сам инпут */}
          <div
            className={`${styles.card} ${styles.cardType}`}
            data-part="side"
            onClick={(e) => {
              if ((e.target as HTMLElement).closest('button')) return
              fromInputRef.current?.focus()
            }}
          >
            <div className={styles.cardHead}>
              <span className={styles.label}>From</span>
              <Balance token={fromTok} balances={balances} />
            </div>
            <div className={styles.cardRow}>
              <input
                ref={fromInputRef}
                className={styles.amount}
                data-size={sizeOf(amount)}
                value={amount}
                placeholder="0"
                onChange={(e) => setAmount(sanitize(e.target.value))}
                inputMode="decimal"
                aria-label="Amount to convert"
              />
              <TokenSelect
                value={fromTok}
                options={CRYPTO}
                onChange={pickFrom}
                ariaLabel="From token"
                balances={balances}
              />
            </div>
            {/* the $ worth of what is typed, under its figure: whole, on a line
                of its own — it is never cut beside a long amount. The line
                keeps its height while there is none, so nothing moves. */}
            <p className={styles.cardNote} data-part="worth">
              {usdWorth != null ? (
                <span className={styles.inlineUsd}>≈ ${fmt(usdWorth)}</span>
              ) : (
                // nothing typed: the line is there all the same, faint
                // (operator, 2026-10-18: "under the input, if nothing is
                // typed it is empty") — and with no rate, a bare dash (the
                // operator, 2026-11-03: no "≈", no "$—")
                <span className={styles.worthEmpty}>{n > 0 ? '—' : '$0.00'}</span>
              )}
            </p>
          </div>

          {/* the flip: exactly in the middle between the two cards */}
          <div className={styles.between} data-part="between">
            <span className={styles.seat}>
              <button
                type="button"
                className={`${soft.btn} ${soft.round} ${styles.betweenBtn}`}
                aria-label="Swap direction"
                onClick={flip}
              >
                <span
                  className={styles.flipIcon}
                  style={{ transform: `rotate(${flips * 180}deg)` }}
                >
                  <ArrowsDownUpIcon size={18} weight="bold" aria-hidden="true" />
                </span>
              </button>
            </span>
          </div>

          <ResultCard
            label="To"
            value={out}
            token={toTok}
            balances={balances}
            rate={cross != null ? `1 ${fromTok} ≈ ${fmtRate(cross)} ${toTok}` : `1 ${fromTok} ≈ —`}
          >
            <TokenSelect
              value={toTok}
              options={CRYPTO}
              onChange={pickTo}
              ariaLabel="To token"
              drop="up"
              balances={balances}
            />
          </ResultCard>
        </div>
      </div>
      <div className={styles.footer}>
        <button type="button" className={`${soft.action} ${styles.cta}`} disabled={n <= 0}>
          {n > 0 ? (
            <>
              <ArrowsDownUpIcon size={17} weight="bold" aria-hidden="true" />
              {/* what will happen, once there is an estimate to stand on */}
              {figs(out != null ? `Convert ${fmtTyped(n)} ${fromTok}` : 'Convert')}
            </>
          ) : (
            'Enter amount'
          )}
        </button>
      </div>
    </>
  )
}
